import axios from 'axios'
import { DateTime } from 'luxon'

import environment from '@/environment'
import { CUSTOMERS_API_URL } from '@/features/site/endpoints/customers'
import type {
  InsightRevenue,
  InsightRevenueFrame,
  InsightRevenuePeriod,
  InsightRevenuePeriodsParams,
  InsightRevenuesByMarketProgramsParams,
  RevenueDataRow,
  RevenuePeriod,
  RevenueTimeSeries,
} from '@/features/site/types/revenue'
import { getLimitRevenueDateTimeRange } from '@/features/site/utils/revenueUtils/getLimitRevenueDateTimeRange'

export const GET_REVENUE_TIME_SERIES_API_ID = 'GET_REVENUE_TIME_SERIES'
export const GET_REVENUE_PERIODS_API_ID = 'GET_REVENUE_PERIODS'
export const GET_INSIGHT_REVENUES_API_ID = 'GET_INSIGHT_REVENUES'

export async function getCustomerRevenuePeriod(
  customerUuid: string,
  location: string | undefined,
): Promise<RevenuePeriod[]> {
  const CUSTOMER_API_URL = `${CUSTOMERS_API_URL}/${customerUuid}`
  const response = await axios.get(`${CUSTOMER_API_URL}/revenues/period`, {
    params: {
      location,
    },
  })

  return response.data
}

export async function getCustomerRevenueTimeSeries(
  customerUuid: string,
  serviceId: number,
  startTime: DateTime,
  endTime: DateTime,
  location: string | undefined,
): Promise<RevenueTimeSeries> {
  const CUSTOMER_API_URL = `${CUSTOMERS_API_URL}/${customerUuid}`
  const response = await axios.get(`${CUSTOMER_API_URL}/revenues`, {
    params: {
      serviceId,
      startTime,
      endTime,
      location,
    },
  })

  return response.data
}

/**
 * Transform one frame of the revenues API response into revenue rows.
 *
 * The API returns frames with two properties: fields and data.
 *
 * fields: Includes metadata information about the frame data. Here an example of how it looks like:
 * 
  "fields": [
        {
          "name": "time",
          "type": "Time",
          "labels": null
        },
        {
          "name": "availableCapacity",
          "type": "Number",
          "labels": [
            {
              "name": "marketProgram",
              "value": "FCR-N CAPACITY"
            },
            {
              "name": "siteUuid",
              "value": "c7cc280c-8ff9-45dc-88f7-f4509f63998d"
            }
          ]
        },
    ]        
 *  data: contains the frames values. it looks like this:

    "data": [
        [1735768800000, 1735772400000],
        [null, 43.69],
    ]
 * 
 * We need to know the index of every field in order to map the data. Based on the previous example, we
 * know that the first index of the array represents time (because is the first index on the fields property), and
 * the second index represents availableCapacity (because it's the second index of the fields property).
 * 
 * In order to make the code on the client side simpler, we transform this information info an array of
 * RevenueDataRow objects. 
 */
function parseRevenueFrame({ data, fields }: InsightRevenueFrame, timezone: string): RevenueDataRow[] | null {
  const dataLength = data?.[0]?.length ?? 0

  if (!data || !dataLength) {
    return null
  }

  // revenueFieldsIdx it's a map that represents the different fields and their index position.
  const revenueFieldsIdx = new Map<string, number>()
  const revenueData: RevenueDataRow[] = []

  for (const [index, field] of fields.entries()) {
    revenueFieldsIdx.set(field.name, index)
  }

  // availableCapacity and soldCapacity are using MW, activatedEnergy is using MWh
  const capacityIndex = revenueFieldsIdx.get('availableCapacity')
  const soldCapacityIndex = revenueFieldsIdx.get('soldCapacity')
  const activatedEnergyIndex = revenueFieldsIdx.get('activatedEnergy')
  const revenueIndex = revenueFieldsIdx.get('soldRevenueInCurrency')
  const timestampIndex = revenueFieldsIdx.get('time')

  // The unit label on the soldRevenueInCurrency field carries the site's ISO currency code (e.g. "NOK", "EUR").
  const currency =
    revenueIndex !== undefined ? (fields[revenueIndex].labels?.find((label) => label.name === 'unit')?.value ?? '') : ''

  for (let i = 0; i < dataLength; i++) {
    const timestamp = timestampIndex !== undefined ? data[timestampIndex][i] : null

    // timestamp should never be null, but just in case we skip that particular data frame
    if (!timestamp) {
      continue
    }
    revenueData.push({
      // TODO: We use the timestamp as internal id. This is being used to identify the data in the UI. We should consider changing this for a uuid property, but
      // that will require changes on the old revenue page as well. We could do it as a follow up task when we remove the old revenue page.
      id: timestamp,
      capacity: capacityIndex !== undefined ? (data[capacityIndex][i] ?? 0) : 0,
      timestamp: DateTime.fromMillis(timestamp).setZone(timezone),
      revenue: revenueIndex !== undefined ? (data[revenueIndex][i] ?? 0) : 0,
      soldCapacity: soldCapacityIndex !== undefined ? (data[soldCapacityIndex][i] ?? 0) : 0,
      activatedEnergy: activatedEnergyIndex !== undefined ? (data[activatedEnergyIndex][i] ?? 0) : 0,
      currency,
    })
  }

  return revenueData
}

/**
 * The API labels every frame with the display name of its market program (e.g. "FCR-N CAPACITY",
 * "Day-ahead", "wholesale_fees"), while the rest of the app uses the enum names (FCR_N_CAPACITY,
 * DAY_AHEAD, WHOLESALE_FEES). Both spell the same name, so they match once upper-cased and with
 * every run of non-alphanumeric characters replaced by an underscore.
 */
export function toMarketProgramName(apiMarketProgram: string): string {
  return apiMarketProgram.toUpperCase().replaceAll(/[^A-Z0-9]+/g, '_')
}

function getFrameMarketProgram(frame: InsightRevenueFrame): string | undefined {
  for (const field of frame.fields) {
    const label = field.labels?.find((fieldLabel) => fieldLabel.name === 'marketProgram')

    if (label) {
      return toMarketProgramName(label.value)
    }
  }

  return undefined
}

/**
 * It gets revenues grouped by market program, with a single request for all the market programs. This
 * function returns a map, where the keys are the requested market programs, and the values are array
 * of revenues. Be aware that when a market program does not have revenues, the value of the map will
 * be null: the API leaves out the frame of a market program without revenues.
 */
export async function getInsightRevenuesByMarketPrograms({
  marketPrograms,
  ...params
}: InsightRevenuesByMarketProgramsParams): Promise<Map<string, RevenueDataRow[] | null>> {
  const revenuesByMarketProgram = new Map<string, RevenueDataRow[] | null>()

  // The API requires at least one market program
  if (marketPrograms.length === 0) {
    return revenuesByMarketProgram
  }

  const defaultDateTimeRange = getLimitRevenueDateTimeRange(params.timezone)
  const endTime = params.endTime?.setZone('UTC') ?? defaultDateTimeRange.endTime
  const startTime = params.startTime?.setZone('UTC') ?? defaultDateTimeRange.startTime

  const response = await axios.get<InsightRevenue>(`${environment.services.insightsManagerApiUrl}/revenues`, {
    params: {
      'market-program': marketPrograms,
      'site-id': params.siteId,
      'start-time': startTime.toMillis(),
      'end-time': endTime.toMillis(),
    },
    // Repeat the parameter for every market program (market-program=A&market-program=B), which is what the API
    // expects. By default axios would send market-program[]=A&market-program[]=B.
    paramsSerializer: { indexes: null },
  })

  const framesByMarketProgram = new Map<string, InsightRevenueFrame>()

  for (const frame of response.data?.frames ?? []) {
    const marketProgram = getFrameMarketProgram(frame)

    if (marketProgram) {
      framesByMarketProgram.set(marketProgram, frame)
    }
  }

  for (const marketProgram of marketPrograms) {
    const frame = framesByMarketProgram.get(toMarketProgramName(marketProgram))

    revenuesByMarketProgram.set(marketProgram, frame ? parseRevenueFrame(frame, params.timezone) : null)
  }

  return revenuesByMarketProgram
}

export async function getInsightRevenuesPeriods(params: InsightRevenuePeriodsParams): Promise<InsightRevenuePeriod[]> {
  // earliestCalculated and latestCalculated are milliseconds in UTC
  const response = await axios.get<{ earliestCalculated: number; latestCalculated: number; marketProgram: string }[]>(
    `${environment.services.insightsManagerApiUrl}/revenues/periods/${params.siteUuid}`,
  )

  return response.data.map((period) => ({
    earliestCalculated: DateTime.fromMillis(period.earliestCalculated).setZone(params.timezone),
    latestCalculated: DateTime.fromMillis(period.latestCalculated).setZone(params.timezone),
    marketProgram: period.marketProgram,
  }))
}
